/* Progressive collection previews: ordinary product links remain the fallback. */
(() => {
  if (window.lsCollectionSwatches) return;
  window.lsCollectionSwatches = true;
  const cache = new Map();
  const membersByCard = new WeakMap();
  const pending = [];
  let active = 0;
  const run = () => {
    while (active < 2 && pending.length) {
      active++;
      const { task, resolve, reject } = pending.shift();
      task().then(resolve, reject).finally(() => { active--; run(); });
    }
  };
  const schedule = task => new Promise((resolve, reject) => {
    pending.push({ task, resolve, reject });
    run();
  });
  function family(card) {
    const key = card.dataset.family;
    if (!cache.has(key)) {
      const request = schedule(async () => {
        const url = new URL(card.dataset.familyUrl, location.origin);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error('Color previews unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = doc.querySelector('[data-ls-family-response]');
        if (!root) throw new Error('Invalid color preview response');
        const result = new Map();
        root.querySelectorAll('template[data-ls-member]').forEach(template => {
          const fragment = template.content;
          const link = fragment.querySelector('[data-ls-swatch]');
          if (link && fragment.querySelector('[data-ls-card-media]') && fragment.querySelector('[data-ls-card-info]')) {
            result.set(template.dataset.lsMember, fragment);
          }
        });
        return result;
      });
      cache.set(key, request);
      request.catch(() => cache.delete(key));
    }
    return cache.get(key);
  }
  async function enhance(card) {
    try {
      const members = await family(card);
      if (!card.isConnected) return;
      if (!members.has(card.dataset.handle) || members.size < 2) { card.dataset.lsSwatchState = 'unavailable'; return; }
      const row = card.querySelector('[data-ls-card-swatches]');
      const links = document.createDocumentFragment();
      members.forEach((fragment, handle) => {
        const link = fragment.querySelector('[data-ls-swatch]').cloneNode(true);
        if (handle === card.dataset.handle) link.setAttribute('aria-current', 'true');
        links.append(link);
      });
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', 'Choose color or design');
      row.replaceChildren(links);
      membersByCard.set(card, members);
      card.dataset.lsSwatchState = 'ready';
      // Reveal the current color within this row without moving the page.
      const selected = row.querySelector('[aria-current="true"]');
      if (selected && row.clientWidth) {
        row.scrollLeft = Math.max(0, selected.offsetLeft + selected.offsetWidth - row.clientWidth);
      }
    } catch (_) {
      // Leave the complete, server-rendered card usable when a family cannot load.
      card.dataset.lsSwatchState = 'unavailable';
    }
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) { observer.unobserve(entry.target); enhance(entry.target); }
    });
  }, { rootMargin: '200px' }) : null;
  function scan(root) {
    const cards = [];
    if (root.matches?.('[data-ls-collection-card][data-family-url]')) cards.push(root);
    cards.push(...root.querySelectorAll('[data-ls-collection-card][data-family-url]'));
    cards.forEach(card => {
      if (card.dataset.lsSwatchState) return;
      card.dataset.lsSwatchState = 'observed';
      if (observer) observer.observe(card); else enhance(card);
    });
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-ls-swatch]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const card = link.closest('[data-ls-collection-card]');
    const fragment = membersByCard.get(card)?.get(link.dataset.lsSwatch);
    if (!fragment) return;
    event.preventDefault();
    if (card.dataset.handle === link.dataset.lsSwatch) return;
    ['media', 'info'].forEach(part => {
      card.querySelector(`[data-ls-card-${part}]`).replaceChildren(...Array.from(fragment.querySelector(`[data-ls-card-${part}]`).childNodes, node => node.cloneNode(true)));
    });
    card.dataset.handle = link.dataset.lsSwatch;
    card.querySelectorAll('[data-ls-swatch]').forEach(item => {
      if (item === link) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
  });
  // Ask Shopify for matching facets across the full collection, not just loaded cards.
  const filterRequests = new WeakMap();
  const staleFilters = new WeakSet();
  function cancelFilterRefresh(form) {
    filterRequests.get(form)?.abort();
    filterRequests.delete(form);
    form.removeAttribute('aria-busy');
    const status = form.querySelector('[data-ls-filter-status]');
    if (status) status.hidden = true;
  }
  function preserveSearchOptions(form) {
    if (!form.querySelector('[name="q"]')) return;
    const current = new URL(location.href);
    ['options[prefix]', 'options[unavailable]'].forEach(name => {
      if (!current.searchParams.has(name) || Array.from(form.elements).some(el => el.name === name)) return;
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = current.searchParams.get(name);
      form.append(input);
    });
  }
  async function refreshFilters(form) {
    cancelFilterRefresh(form);
    staleFilters.add(form);
    preserveSearchOptions(form);
    const controller = new AbortController();
    filterRequests.set(form, controller);
    const timer = setTimeout(() => controller.abort(), 15000);
    const status = form.querySelector('[data-ls-filter-status]');
    form.setAttribute('aria-busy', 'true');
    if (status) { status.hidden = false; status.textContent = 'Updating choices…'; }
    try {
      const url = new URL(form.action, location.origin);
      url.search = new URLSearchParams(new FormData(form)).toString();
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Filters unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('[data-ls-filter-form]');
      if (!next) throw new Error('Missing filters');
      if (filterRequests.get(form) !== controller || !form.isConnected) return;
      const opened = new Set(Array.from(form.querySelectorAll('details[open]'), el => el.dataset.lsFilterSection));
      const focused = document.activeElement;
      const focusKey = form.contains(focused) ? { name: focused.name, value: focused.value } : null;
      const body = form.querySelector('.ls-filter-drawer__body');
      const scrollTop = body.scrollTop;
      form.querySelectorAll('[data-ls-filter-section]').forEach(el => el.remove());
      next.querySelectorAll('[data-ls-filter-section]').forEach(el => {
        el.open = opened.has(el.dataset.lsFilterSection);
        body.append(el);
      });
      if (focusKey) {
        Array.from(form.querySelectorAll('input')).find(el => el.name === focusKey.name && el.value === focusKey.value)?.focus({ preventScroll: true });
      }
      body.scrollTop = scrollTop;
      staleFilters.delete(form);
      if (status) status.hidden = true;
    } catch (_) {
      if (filterRequests.get(form) === controller && status) {
        status.hidden = false;
        status.textContent = 'Choices could not refresh. Select Show products to apply your filters.';
      }
    } finally {
      clearTimeout(timer);
      if (filterRequests.get(form) === controller) {
        filterRequests.delete(form);
        form.removeAttribute('aria-busy');
      }
    }
  }
  document.addEventListener('change', event => {
    const form = event.target.closest('[data-ls-filter-form]');
    if (form && event.target.matches('input[type="checkbox"]')) refreshFilters(form);
  });
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-ls-filter-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      dialog?.showModal();
      const form = dialog?.querySelector('form');
      if (form && staleFilters.has(form)) refreshFilters(form);
      return;
    }
    const close = event.target.closest('[data-ls-filter-close]');
    const backdrop = event.target.matches?.('dialog.ls-filter-drawer') ? event.target : null;
    if (close || backdrop) {
      const dialog = backdrop || close.closest('dialog');
      const form = dialog.querySelector('form');
      cancelFilterRefresh(form);
      dialog.close();
      return;
    }
    const clear = event.target.closest('[data-ls-filter-clear]');
    if (clear) {
      clear.closest('form').querySelectorAll('input').forEach(input => {
        if (input.type === 'checkbox') input.checked = false;
        else if (input.type === 'number') { input.value = ''; input.setCustomValidity(''); }
      });
      refreshFilters(clear.closest('form'));
    }
  });
  document.addEventListener('cancel', event => {
    if (event.target.matches?.('.ls-filter-drawer')) cancelFilterRefresh(event.target.querySelector('form'));
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-ls-filter-form]')) return;
    preserveSearchOptions(form);
    const min = form.querySelector('input[name="filter.v.price.gte"]');
    const max = form.querySelector('input[name="filter.v.price.lte"]');
    if (min?.value && max?.value && Number(min.value) > Number(max.value)) {
      event.preventDefault();
      max.setCustomValidity('Enter a maximum price greater than or equal to the minimum.');
      max.reportValidity();
      return;
    }
    form.querySelectorAll('input[type="number"]').forEach(input => { if (!input.value) input.disabled = true; });
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview && !form.querySelector('[name="preview_theme_id"]')) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = 'preview_theme_id'; input.value = preview; form.append(input);
    }
  });
  document.addEventListener('input', event => {
    const form = event.target.closest('[data-ls-filter-form]');
    if (form) form.querySelectorAll('input[type="number"]').forEach(input => input.setCustomValidity(''));
  });
  // This scoped loader avoids the legacy theme's repeated click handlers after filtering.
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-ls-load-more]');
    if (!button || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;
    const main = button.closest('[data-collection-main]');
    const grid = main?.querySelector('.collection-matrix');
    const pagination = button.closest('[data-ls-pagination]');
    if (!grid || !pagination) return;
    const error = pagination.querySelector('[data-ls-load-error]');
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Loading…';
    error.hidden = true;
    try {
      const url = new URL(button.href, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error('Load failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextMain = doc.querySelector('[data-collection-main]');
      const cards = Array.from(nextMain?.querySelectorAll('.collection-matrix > [data-ls-collection-card], .collection-matrix > [data-ls-search-result]') || []);
      if (!cards.length) throw new Error('Missing products');
      if (!button.isConnected || !grid.isConnected) return; // A filter/sort change superseded this request.
      const position = { left: window.scrollX, top: window.scrollY };
      grid.append(...cards);
      const count = grid.querySelectorAll('[data-ls-collection-card], [data-ls-search-result]').length;
      const countLabel = pagination.querySelector('[data-ls-product-count]');
      if (countLabel) countLabel.textContent = `Showing ${count} of ${pagination.dataset.total} ${pagination.dataset.lsResultLabel || 'products'}`;
      const next = nextMain.querySelector('[data-ls-load-more]');
      if (next) button.setAttribute('href', next.getAttribute('href'));
      else button.hidden = true;
      if (event.detail === 0 || button.hidden) cards[0].querySelector('a')?.focus({ preventScroll: true });
      window.scrollTo(position);
    } catch (_) {
      if (button.isConnected) {
        error.textContent = 'Products could not load. Please try again.';
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.textContent = 'Load More';
    }
  });
  scan(document);
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
    if (node.nodeType === 1) scan(node);
  }))).observe(document.body, { childList: true, subtree: true });
})();
